import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Package, CheckCircle2, Clock, Truck, Loader2, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
    { id: "received", label: "Received", icon: Package },
    { id: "accepted", label: "Accepted", icon: CheckCircle2 },
    { id: "manufacturing", label: "Manufacturing", icon: Clock },
    { id: "qc", label: "Quality Check", icon: CheckCircle2 },
    { id: "dispatched", label: "Dispatched", icon: Truck },
    { id: "delivered", label: "Delivered", icon: MapPin },
];

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

export default function Track() {
    const { caseNumber } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        axios.get(`${API}/track/${caseNumber}`)
            .then(({ data }) => setData(data))
            .catch((e) => setErr(e?.response?.status === 404 ? "Not found" : "Failed to load"))
            .finally(() => setLoading(false));
    }, [caseNumber]);

    if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={28} /></div>;
    if (err === "Not found") {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-5">
                <div className="text-center max-w-md" data-testid="track-not-found">
                    <h1 className="font-display text-3xl text-slate-900 tracking-tight">Case not found</h1>
                    <p className="mt-2 text-slate-600 text-[14px]">We couldn't find a case with id <span className="font-mono-tr">{caseNumber}</span>.</p>
                    <Link to="/" className="tr-btn-ghost mt-5 inline-flex">Back to home</Link>
                </div>
            </div>
        );
    }

    const timelineMap = Object.fromEntries((data.timeline || []).map((t) => [t.status, t.at]));
    const currentIdx = Math.max(STEPS.findIndex((s) => s.id === data.status), 0);

    return (
        <div data-testid="track-page" className="min-h-[80vh] bg-slate-50/60">
            <div className="max-w-[760px] mx-auto px-5 lg:px-8 py-12 lg:py-20">
                <div className="rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                    <div className="p-8 lg:p-10 bg-gradient-to-br from-cyan-50 via-white to-slate-50">
                        <div className="font-mono-tr text-[11px] tracking-[0.22em] uppercase text-slate-500">· Order tracker</div>
                        <h1 className="font-display font-light text-3xl lg:text-4xl text-slate-900 tracking-tight mt-1">{data.product}</h1>
                        <div className="mt-2 text-slate-600 text-[14px] font-mono-tr">{data.case_number}</div>
                        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-600 text-white text-[12px] font-medium">
                            <span className="h-1.5 w-1.5 rounded-full bg-white tr-pulse-dot" />
                            Status · {data.status}
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="p-8 lg:p-10">
                        <div className="space-y-5">
                            {STEPS.map((s, i) => {
                                const done = i <= currentIdx;
                                const at = timelineMap[s.id];
                                const Icon = s.icon;
                                return (
                                    <div key={s.id} className="flex items-start gap-4" data-testid={`track-step-${s.id}`}>
                                        <div className={`relative h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                                            <Icon size={18} />
                                            {i < STEPS.length - 1 && (
                                                <span className={`absolute top-full mt-1 left-1/2 -translate-x-1/2 w-px h-7 ${done && i < currentIdx ? "bg-cyan-500" : "bg-slate-200"}`} />
                                            )}
                                        </div>
                                        <div className="flex-1 pt-1">
                                            <div className={`text-[15px] font-medium ${done ? "text-slate-900" : "text-slate-500"}`}>{s.label}</div>
                                            {at && <div className="text-[12.5px] font-mono-tr text-slate-500 mt-0.5">{formatDate(at)}</div>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="p-6 lg:p-8 bg-slate-50/60 border-t border-slate-100 grid sm:grid-cols-3 gap-3 text-[13px]">
                        <div><div className="font-mono-tr text-[10px] tracking-[0.2em] uppercase text-slate-500">Clinic</div><div className="mt-0.5 text-slate-900 font-medium">{data.clinic_name || "—"}</div></div>
                        <div><div className="font-mono-tr text-[10px] tracking-[0.2em] uppercase text-slate-500">Patient</div><div className="mt-0.5 text-slate-900 font-medium">{data.patient}</div></div>
                        <div><div className="font-mono-tr text-[10px] tracking-[0.2em] uppercase text-slate-500">SLA</div><div className="mt-0.5 text-slate-900 font-medium">{data.expected_delivery_hours} hours</div></div>
                    </div>
                </div>

                <div className="text-center mt-7 text-[13px] text-slate-500">
                    Issued by <Link to="/" className="text-slate-900 hover:text-cyan-700 font-medium">Toothrocket Labs</Link> · Doha · Qatar
                </div>
            </div>
        </div>
    );
}
